<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 id="renal-cystic-test-title">
        {{ testName }}
      </h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="renal-cystic-test-grid"
      >
        <v-container>
          <v-row>
            <v-col class="custom-col">
              <p>
                This Medicare funded
                <a href="https://www.vcgs.org.au/node/1436" target="_blank"
                  >Heritable Cystic Kidney Disease</a
                >
                test analyses genes associated with macro cystic and micro
                cystic renal disease (nephronophthisis) via Exome sequencing.
                Medicare item number: 73401
              </p>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col class="custom-col">
              <v-alert text color="info">
                <table>
                  <tr>
                    <td>
                      <b>Eligibility criteria:</b>
                    </td>
                  </tr>
                  <tr>
                    Requests must be made by a consultant clinical geneticist or
                    specialist nephrologist for a Medicare eligible patient with
                    a renal abnormality associated with cystic kidney disease
                    who is strongly suspected of having a monogenic condition.
                  </tr>
                  <br />
                  <tr>
                    If your patient does not meet these criteria, you can still
                    order and pay by selecting Exome Focused and with the Renal
                    Cystic Disease_Superpanel gene panel.
                  </tr>
                </table>
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical features</span>
              <template v-if="brokenPhenotypesApi">
                <BrokenApiAlert type="phenotypes" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="phenotypes"
                  :id="testId + '.phenotypes'"
                  :items="phenotypeList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  persistent-hint
                  deletable-chips
                  @input="searchInputPhenotypes = null"
                  :search-input.sync="searchInputPhenotypes"
                  outlined
                  hide-details="auto"
                  @update:search-input="handlePhSearchChange"
                  :filter="filterPhItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingHp">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ phSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please start typing the main clinical features. e.g.
                  intellectual disability. Select the most appropriate HPO term
                  from those suggested. Detailed clinical information helps us
                  diagnose patients more accurately. The phenotype's are sourced
                  from hpo.jax.org.</span
                >
              </template>
              <template v-if="badSearchQuery">
                <BadSearchQueryAlert/>
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                rows="1"
                auto-grow
                v-model="familyHx"
                :id="testId + '.familyHx'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. affected status of the parents, parental consanguinity,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes</span>
              <v-textarea
                rows="2"
                auto-grow
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>
        </v-container>

        <RenalCysticConsent
          ref="renalCysticConsent"
          lazy-validation-id="renal-cystic-consent-section"
          :is-trio="false"
          :force-form-dirty="forceFormDirty"
          :testName="testName"
          :testId="testId"
        >
        </RenalCysticConsent>

        <TestPriceContainer
          :price="price"
          :showReanalysisAlert="showReanalysisAlert"
        />

        <v-btn
          class="primary-btn btn-padding-medium top-spaced-item"
          @click="goToNextSection()"
        >
          Continue to next section
        </v-btn>
      </v-form>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import PhenotypesMixin from "../../mixins/PhenotypesMixin";
import GenePanelMixin from "../../mixins/GenePanelMixin.vue";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import RenalCysticConsent from "./RenalCysticConsentContainer";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceContainer from "../common/TestPriceContainer";
import { EventBus } from "@/event-bus";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import GenesMixin from "../../mixins/GenesMixin.vue";
import BrokenApiAlert from "../common/BrokenApiAlert.vue";
import BadSearchQueryAlert from "../common/BadSearchQueryAlert.vue";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
    showReanalysisAlert: Boolean,
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.renalCysticTestPricing = await this.execRetrieveJson(
      "json/pricing/renal_cystic_pricing.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");

    const element = document.getElementById("renal-cystic-test-title");

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }

    this.urgency = "Standard";
    this.familyType = "Singleton";
    this.analysisType = "Exome Focused";

    const pricing = this.renalCysticTestPricing[this.urgency][this.familyType][
      this.analysisType
    ];

    this.price = pricing.price;
    this.testCode = pricing.testCode;
    this.medicare = pricing.medicare;

    this.$emit("toggleTotalPrice", this.price, this.testId);
    EventBus.$emit("medicareRenalCysticStatus", true);
  },
  mixins: [FormRulesMixin, PhenotypesMixin, RetrieveJSONMixin, GenesMixin],
  name: "",
  components: { RenalCysticConsent, TestPriceContainer, BrokenApiAlert, BadSearchQueryAlert },
  data: () => ({
    valid: false,
    urgent: false,
    urgentReason: "",
    renalCysticTestPricing: null,
    cgApproved: true,
    cgDetails: "",
    genePanels: ["Renal Cystic Disease_SuperPanel"],
    phenotypes: [],
    familyHx: "",
    clinicalNotes: "",
    ageOfOnset: "",
    urgency: null,
    familyType: null,
    analysisType: null,
    indGenes: [],
    billTo: "MEDICARE",
    searchInputGenes: null,
    searchInputPhenotypes: null,
    price: null,
    testCode: null,
    medicare: true,
  }),
  methods: {
    isFormValid: function() {
      if (typeof this.$refs.renalCysticConsent !== "undefined") {
        return this.$refs.renalCysticConsent.isFormValid();
      }
      //if consent type is undefined the component ref has not been rendered to the DOM because the panel
      //hasnt opened, so return false
      else return false;
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      let familyDetails = [];

      for (let [familyKey, familyComponent] of Object.entries(this.$refs)) {
        if (familyComponent && familyKey.substring(0, 12) === "familyMember") {
          familyDetails.push(familyComponent.transferData());
        }
      }

      // Convert hardcoded gene panels value to "<panelId> - <panelName>"
      const finalGenePanels =
        this.genePanels[0] === "Renal Cystic Disease_SuperPanel"
          ? "263 - Renal Cystic Disease_SuperPanel"
          : "";

      let result = {
        urgent: this.urgent,
        urgency: this.urgency,
        urgentReason: "",
        testType: this.testId,
        familyType: this.familyType,
        analysisType: this.analysisType,
        rapid: this.rapidTest,
        genePanels: finalGenePanels,
        indGenes: this.getFinalIndGenes(this.indGenes),
        phenotypes: this.getFinalPhenotypes(this.phenotypes),
        ageOfOnset: this.ageOfOnset,
        familyHx: this.familyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
        cgApproved: this.cgApproved,
        cgDetails: this.cgDetails,
        billTo: this.billTo,
      };

      // Consent data is present, except if the test is medicare but not approved by gc
      if (this.$refs.renalCysticConsent) {
        const consentData = this.$refs.renalCysticConsent.transferData();

        // assign the data fields from consent direct into the top level of our data
        Object.assign(result, consentData);
      }

      return result;
    },
  },
};
</script>
